/**
 * Экран управления общим пулом опыта
 * Позволяет выбирать существо и тратить опыт из пула для повышения уровня
 */

import { logger } from "../../utils/logger.js";

export function createExpManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  getUserCollection,
  documentRef = document
}) {
  
  /**
   * Загружает пул опыта
   */
  async function loadExpPool() {
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/exp/pool`, {
        method: 'GET',
        headers: buildAuthHeaders()
      });

      // safeFetch оборачивает ответ, проверяем вложенную структуру
      if (response.success && response.data) {
        if (response.data.data) {
          return response.data.data.exp_pool || 0;
        } else if (response.data.success && response.data.data) {
          return response.data.data.exp_pool || 0;
        } else if (response.data.success) {
          return response.data.exp_pool || 0;
        } else {
          return response.data.exp_pool || 0;
        }
      }
      
      return 0;
    } catch (error) {
      logger.error('[ExpManager] Ошибка загрузки пула опыта:', error);
      return 0;
    }
  }

  /**
   * Применяет опыт к существу
   */
  async function applyExpToCreature(creatureId, amount) {
    const telegramWebApp = getTelegramWebApp();
    
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/exp/apply`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ creatureId, amount })
      });

      // safeFetch оборачивает ответ, проверяем вложенную структуру
      let data = null;
      if (response.success && response.data) {
        if (response.data.data) {
          data = response.data.data;
        } else if (response.data.success && response.data.data) {
          data = response.data.data;
        } else {
          data = response.data;
        }
      }
      
      if (response.success && data && data.success) {
        // Обновляем данные пользователя
        const currentUser = getCurrentUser();
        if (currentUser && data.coins !== undefined) {
          const updatedUser = { ...currentUser, coins: data.coins, exp_pool: data.exp_pool };
          setCurrentUser(updatedUser);
          updateUserBalancesUI(updatedUser);
        }
        
        // Показываем уведомление
        const lootboxManager = window.lootboxManager;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          if (data.levelUp) {
            lootboxManager.showLootboxNotification(
              '📈 Уровень повышен!',
              `+${data.expAdded || amount} опыта\nНовый уровень: ${data.newLevel || 0}\nПотрачено монет: ${data.coinsSpent || 0}`
            );
          } else {
            lootboxManager.showLootboxNotification(
              '📈 Опыт добавлен',
              `+${data.expAdded || amount} опыта\nНовый опыт: ${data.newExp || 0}/100\nПотрачено монет: ${data.coinsSpent || 0}`
            );
          }
        } else if (telegramWebApp?.showAlert) {
          if (data.levelUp) {
            telegramWebApp.showAlert(`✅ Существо повысило уровень! Новый уровень: ${data.newLevel}\nПотрачено монет: ${data.coinsSpent}`);
          } else {
            telegramWebApp.showAlert(`✅ Добавлено ${data.expAdded || amount} опыта существу!\nНовый опыт: ${data.newExp || 0}/100\nПотрачено монет: ${data.coinsSpent || 0}`);
          }
        }
        
        return true;
      } else {
        const errorMessage = data?.message || response.error || 'Ошибка применения опыта';
        const lootboxManager = window.lootboxManager;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
        } else if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(errorMessage);
        }
        return false;
      }
    } catch (error) {
      logger.error('[ExpManager] Ошибка применения опыта:', error);
      const errorMessage = 'Ошибка применения опыта. Попробуйте позже.';
      const lootboxManager = window.lootboxManager;
      if (lootboxManager && lootboxManager.showLootboxNotification) {
        lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
      } else if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(errorMessage);
      }
      return false;
    }
  }

  /**
   * Рассчитывает стоимость повышения уровня
   */
  function calculateLevelUpCost(currentLevel, currentExp) {
    const expNeeded = 100 - currentExp;
    // Прогрессивная стоимость: 10 * (уровень^1.5)
    const coinsNeeded = Math.floor(10 * Math.pow(currentLevel, 1.5));
    return { expNeeded, coinsNeeded };
  }

  /**
   * Рендерит экран управления опытом
   */
  async function renderExpManager() {
    const container = documentRef.getElementById('exp-manager-container');
    if (!container) {
      console.error('[ExpManager] Контейнер не найден');
      return;
    }

    try {
      const expPool = await loadExpPool();
      const collection = await getUserCollection();

      let html = `
        <div style="
          background: var(--tg-theme-bg-color, #1a1a2e);
          border: 1px solid var(--tg-theme-hint-color, rgba(255,255,255,0.2));
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
        ">
          <div style="font-size: 18px; font-weight: 600; margin-bottom: 8px;">
            📈 Общий пул опыта
          </div>
          <div style="font-size: 32px; font-weight: 700; color: #4dfffd;">
            ${expPool}
          </div>
          <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">
            Выберите существо и количество опыта для применения
          </div>
        </div>
        
        <div style="display: grid; gap: 12px;">
    `;
    
    collection.forEach(creature => {
      // Важно: getUserCreature ожидает creature_id (ID существа), а не id (ID записи в user_creatures)
      // В коллекции: id = запись в user_creatures, creatureId = ID существа
      const creatureId = creature.creatureId || creature.creature_id || creature.id;
      const currentLevel = creature.level || 1;
      const currentExp = creature.exp || 0;
      const maxLevel = 10;
      const expPercent = Math.floor((currentExp / 100) * 100);
      const { expNeeded, coinsNeeded } = calculateLevelUpCost(currentLevel, currentExp);
      
      // Можно применять опыт только если не достигнут максимальный уровень
      const canAdd = expPool > 0 && currentLevel < maxLevel;
      const maxCanAdd = Math.min(expPool, expNeeded);
      
      html += `
        <div style="
          background: var(--tg-theme-bg-color, #1a1a2e);
          border: 1px solid var(--tg-theme-hint-color, rgba(255,255,255,0.2));
          border-radius: 8px;
          padding: 12px;
        ">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
            <div style="font-size: 32px;">${creature.visual || '🦉'}</div>
            <div style="flex: 1;">
              <div style="font-weight: 600; font-size: 14px;">${creature.name || 'Существо'} ${creatureId ? `#${creatureId}` : ''}</div>
              <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                Уровень ${currentLevel}${currentLevel >= maxLevel ? ' (макс.)' : ''}
              </div>
            </div>
          </div>
          
          <div style="margin-bottom: 8px;">
            <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 4px;">
              <span>Опыт</span>
              <span>${currentExp}/100 (${expPercent}%)</span>
            </div>
            <div style="
              width: 100%;
              height: 8px;
              background: var(--tg-theme-hint-color, rgba(255,255,255,0.1));
              border-radius: 4px;
              overflow: hidden;
            ">
              <div style="
                width: ${expPercent}%;
                height: 100%;
                background: ${expPercent > 50 ? '#4dfffd' : expPercent > 25 ? '#ffcc00' : '#ff4d4d'};
                transition: width 0.3s;
              "></div>
            </div>
          </div>
          
          ${canAdd ? `
            <div style="margin-bottom: 8px; padding: 8px; background: rgba(77, 255, 253, 0.1); border-radius: 6px; font-size: 12px;">
              <div style="margin-bottom: 4px;">Для повышения уровня нужно:</div>
              <div>📈 Опыт: ${expNeeded}</div>
              <div>💰 Монеты: ${coinsNeeded}</div>
            </div>
            <div style="display: flex; gap: 8px;">
              <button onclick="window.applyExpToCreature(${creatureId}, ${Math.min(10, maxCanAdd)})" style="
                flex: 1;
                background: var(--tg-theme-button-color, #4dfffd);
                color: var(--tg-theme-button-text-color, #000);
                border: none;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 600;
                cursor: pointer;
                transition: transform 0.2s, opacity 0.2s;
              " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                +${Math.min(10, maxCanAdd)} опыта
              </button>
              ${maxCanAdd > 10 ? `
                <button onclick="window.applyExpToCreature(${creatureId}, ${maxCanAdd})" style="
                  flex: 1;
                  background: var(--tg-theme-button-color, #4dfffd);
                  color: var(--tg-theme-button-text-color, #000);
                  border: none;
                  padding: 8px 12px;
                  border-radius: 6px;
                  font-size: 12px;
                  font-weight: 600;
                  cursor: pointer;
                  transition: transform 0.2s, opacity 0.2s;
                " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                  +${maxCanAdd} (до уровня)
                </button>
              ` : ''}
            </div>
          ` : `
            <div style="
              text-align: center;
              padding: 8px;
              background: var(--tg-theme-hint-color, rgba(255,255,255,0.1));
              border-radius: 6px;
              font-size: 12px;
              color: var(--tg-theme-hint-color);
            ">
              ${currentLevel >= maxLevel ? '✅ Существо достигло максимального уровня (10)' : '⚠️ В пуле нет опыта. Получите опыт из лутбоксов'}
            </div>
          `}
        </div>
      `;
    });
    
    html += `</div>`;
    
    // Используем domCache для оптимизации обновления
    const domCache = window.__domCacheManager;
    if (domCache) {
      domCache.updateHTML('#exp-manager-container', html, documentRef);
    } else {
      container.innerHTML = html;
    }
    } catch (error) {
      logger.error('[ExpManager] Ошибка рендеринга:', error);
      const domCache = window.__domCacheManager;
      const errorHTML = `
        <div style="padding: 40px; text-align: center; color: var(--tg-theme-hint-color);">
          <div style="font-size: 48px; margin-bottom: 16px;">📈</div>
          <div style="font-size: 18px; margin-bottom: 8px;">Ошибка загрузки</div>
          <div style="font-size: 14px;">Не удалось загрузить данные. Попробуйте позже.</div>
        </div>
      `;
      if (domCache) {
        domCache.updateHTML('#exp-manager-container', errorHTML, documentRef);
      } else {
        container.innerHTML = errorHTML;
      }
    }
  }

  return {
    renderExpManager,
    applyExpToCreature,
    loadExpPool
  };
}

